import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const faqs = [
  {
    section: 'General',
    items: [
      {
        q: 'What is MannaPlag?',
        a: 'MannaPlag is an AI-powered plagiarism detection and research integrity platform designed for students, researchers, educators, institutions, publishers, and journals. It compares submitted documents against a continuously expanding scholarly corpus to identify similarities and promote academic integrity.',
      },
      {
        q: 'Who can use MannaPlag?',
        a: 'MannaPlag is suitable for students, researchers, faculty, universities and colleges, publishers, journals, research institutes, and government organisations.',
      },
      {
        q: 'What makes MannaPlag different?',
        a: 'MannaPlag combines AI-powered similarity detection, semantic analysis, institutional repositories, and publisher-grade APIs with a continuously growing academic corpus of 50+ million scholarly documents and records, making it a comprehensive and affordable research integrity platform.',
      },
    ],
  },
  {
    section: 'Academic Corpus',
    items: [
      {
        q: 'How large is the MannaPlag database?',
        a: 'MannaPlag currently indexes over 50 million scholarly documents and records, making it one of the largest continuously growing academic plagiarism detection corpora.',
      },
      {
        q: 'How often is the database updated?',
        a: 'Our academic corpus is continuously updated through automated harvesting from trusted scholarly repositories and publishing platforms, ensuring newly published research is regularly indexed.',
      },
      {
        q: 'What sources are included?',
        a: 'MannaPlag compares submissions against journal articles, conference papers, theses and dissertations, institutional repositories, open access research, public web sources, publisher content (where available), and continuously harvested scholarly repositories.',
      },
      {
        q: 'Why does a large corpus matter?',
        a: 'A larger and continuously updated scholarly corpus increases the likelihood of identifying matching or similar content across published research, theses, conference papers, and institutional repositories, resulting in more comprehensive similarity analysis.',
      },
    ],
  },
  {
    section: 'Similarity Detection',
    items: [
      {
        q: 'How does MannaPlag detect plagiarism?',
        a: 'MannaPlag uses advanced AI-powered similarity detection to compare submitted documents against its academic corpus and identify exact text matches, paraphrased content, similar sentence structures, and reused academic content.',
      },
      {
        q: 'Does MannaPlag detect AI-generated content?',
        a: 'Yes. MannaPlag includes AI-generated content detection to help identify text that may have been created using generative AI tools such as ChatGPT, Claude, and GPT-4.',
      },
      {
        q: 'Does it detect paraphrased plagiarism?',
        a: 'Yes. Semantic analysis helps identify rewritten content that preserves the original meaning even when the wording has been significantly changed.',
      },
      {
        q: 'Does a high similarity score always mean plagiarism?',
        a: 'No. Similarity reports highlight matching text, but properly cited quotations, references, and common academic phrases may legitimately contribute to the similarity score. Human judgment is always required.',
      },
      {
        q: 'Can references and quotations be excluded?',
        a: 'Yes. Users can exclude references, bibliographies, quotations, small matches, and common phrases from the similarity calculation.',
      },
    ],
  },
  {
    section: 'Documents',
    items: [
      {
        q: 'Which file formats are supported?',
        a: 'Supported formats include DOC, DOCX, and PDF.',
      },
      {
        q: 'Is there a file size limit?',
        a: 'The maximum file size and word count depend on your subscription plan.',
      },
      {
        q: 'How quickly are reports generated?',
        a: 'Most reports are generated within a few minutes, depending on document size and system load.',
      },
    ],
  },
  {
    section: 'Institutions',
    items: [
      {
        q: 'Can multiple faculty members use one account?',
        a: 'Yes. Institutional subscriptions support multiple users with role-based permissions.',
      },
      {
        q: 'Can institutions create their own repository?',
        a: 'Yes. Institutions can maintain private repositories for future similarity comparisons.',
      },
      {
        q: 'Does MannaPlag integrate with Learning Management Systems?',
        a: 'Yes. LMS integrations are available through supported platforms and APIs.',
      },
    ],
  },
  {
    section: 'Publishers',
    items: [
      {
        q: 'Does MannaPlag offer an API?',
        a: 'Yes. Publisher subscriptions include REST APIs for editorial workflow integration.',
      },
      {
        q: 'Does MannaPlag support OJS?',
        a: 'Yes. MannaPlag integrates with Open Journal Systems (OJS).',
      },
      {
        q: 'Can manuscripts be screened automatically?',
        a: 'Yes. Publishers can automatically screen manuscripts before peer review through both the web interface and API.',
      },
    ],
  },
  {
    section: 'Privacy & Security',
    items: [
      {
        q: 'Who owns uploaded documents?',
        a: 'Users always retain ownership of their intellectual property. MannaPlag does not claim any rights over submitted documents.',
      },
      {
        q: 'Are uploaded documents secure?',
        a: 'Yes. MannaPlag employs industry-standard security measures to protect uploaded documents and user data.',
      },
      {
        q: 'Are documents shared with third parties?',
        a: 'No. Documents are never shared without user authorisation, except where required by applicable law.',
      },
      {
        q: 'Are uploaded documents stored permanently?',
        a: 'Storage policies depend on the subscription plan and repository settings selected by the customer.',
      },
    ],
  },
  {
    section: 'Billing',
    items: [
      {
        q: 'Is there a free plan?',
        a: 'Yes. Every category includes a Free plan with one plagiarism check per month for evaluation purposes.',
      },
      {
        q: 'Can I purchase additional checks?',
        a: 'Yes. Additional plagiarism checks can be purchased once your annual allocation is exhausted.',
      },
      {
        q: 'Do unused checks roll over?',
        a: 'No. Annual check allocations expire at the end of the subscription period unless otherwise specified.',
      },
      {
        q: 'Are discounts available?',
        a: 'Educational institutions, publisher groups, and consortiums may qualify for volume-based pricing. Contact us to discuss.',
      },
    ],
  },
  {
    section: 'Support & Technical',
    items: [
      {
        q: 'How can I contact support?',
        a: 'Support is available via email and our support portal. You can reach us through the Contact page.',
      },
      {
        q: 'Can I cancel my subscription?',
        a: 'Yes. Annual subscriptions may be cancelled before renewal in accordance with our Terms of Service.',
      },
      {
        q: 'Can MannaPlag integrate into our existing systems?',
        a: 'Yes. MannaPlag supports integration with Learning Management Systems (LMS), Open Journal Systems (OJS), editorial management systems, institutional portals, and custom enterprise applications via REST APIs.',
      },
    ],
  },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-4 py-4 text-left"
      >
        <span className="text-[13px] font-semibold text-gray-800 leading-snug">{q}</span>
        <span className="text-gray-400 flex-shrink-0 mt-0.5 text-[16px] font-light">
          {open ? '−' : '+'}
        </span>
      </button>
      {open && (
        <p className="text-[13px] text-gray-500 leading-relaxed pb-4">{a}</p>
      )}
    </div>
  );
}

export default function FAQPage() {
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.flatMap(s =>
      s.items.map(i => ({
        '@type': 'Question',
        name: i.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: i.a,
        },
      }))
    ),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Frequently Asked Questions: Plagiarism & AI Checker"
        description="Get clear answers about MannaPlag plagiarism checking, AI detection accuracy, supported file types (PDF/DOCX), privacy guarantees, and pricing."
        canonicalPath="/faqs"
        keywords="plagiarism checker faq, how does ai detection work, does mannaplag save papers, turnitin vs mannaplag, plagiarism check confidentiality"
        jsonLd={faqJsonLd}
      />
      {/* Hero */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-[36px] font-black text-gray-900 mb-4 tracking-tight">Frequently asked questions</h1>
          <p className="text-[15px] text-gray-500 max-w-xl mx-auto">
            Everything you need to know about MannaPlag. Can't find what you're looking for?{' '}
            <Link to="/contact" className="text-blue-600 font-semibold hover:underline">Contact us</Link>.
          </p>
        </div>
      </div>

      {/* FAQ sections */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col gap-12">
          {faqs.map(section => (
            <div key={section.section}>
              <h2 className="text-[11px] font-bold text-blue-600 uppercase tracking-widest mb-4">
                {section.section}
              </h2>
              <div className="bg-white border border-gray-100 rounded-xl px-6">
                {section.items.map(item => (
                  <FAQItem key={item.q} q={item.q} a={item.a} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gray-50 rounded-lg p-10 border border-gray-100">
          <h2 className="text-[20px] font-black text-gray-900 mb-3">Still have questions?</h2>
          <p className="text-[13px] text-gray-500 mb-6">Our team is happy to help with anything not covered here.</p>
          <Link
            to="/contact"
            className="inline-block bg-navy text-white text-[13px] font-bold px-6 py-3 rounded-xl hover:opacity-90 transition-opacity"
          >
            Get in touch
          </Link>
        </div>
      </div>
    </div>
  );
}
